import { Logger, engineComponent, ArgValidator } from "neo-common";
import { Communication } from "neo-games";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../Utils/FrameTimer";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { Symbol } from "./Symbol";
import { Tweens } from "../Components/Effects/Tweens";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ReelConfigurationAttributes } from "../Components/Configuration";

type IReel = Communication.Game.IReel;

/**
 * Class reel represent reel symbols rotation.
 * @class Reel
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class Reel extends BaseElement {
    // event for machine when reel stopped
    static ReelStopped = "reelStopped";

    // symbol width without offsetX
    private _symbolWidth: number;
    // symbol height without offsetY
    private _symbolHeight: number;
    // number of symbols which will be visible on screen during the rotation
    private _visibleSymbolCount: number;
    // index of reel from left
    private _reelIndex: number;
    // array of stopping symbols ids
    private _stoppedSymbolArray: Array<number>;
    // array of actual rendered symbols
    private _symbolArray: Array<ImageElement>;
    // pre roll height in pixels
    private _preRoll: number;
    // pre roll speed in frames
    private _preRollSpeed: number;
    // post roll height in pixels
    private _postRoll: number;
    // post roll speed in frames
    private _postRollSpeed: number;
    // speed of rotation in frames
    private _speed: number;
    // speed when force stop is applied in frames
    private _forceSpeed: number;
    private _forceSpeedMultipler: number;

    // these variables defined reel rotation behaviour
    private _spinning: boolean;
    public _stopping: boolean;
    // stopped is public to information for machine and represents that all symbols finished rotation and stopped
    stopped: boolean;

    // index for adding symbols to rotation
    private _actualIndex: number;
    // helper to remember last index for non visible symbols on reel rotation
    private _lastTopIndex: number;
    // helper to start pre roll
    private _itemCounter: number;
    // helper to add right symbols when rotation is stopped
    private _stoppingCounter: number;

    // delay to start rotation in frames
    private _startDelay: number;
    // delay to stop rotation in frames
    private _stopDelay: number;
    // timer for delayed stopping of this reel
    private _stopTimer: FrameTimerEventEmitter;

    // array of symbols for rotation
    private _reel: Array<number>;
    // pool symbols create instances of symbols which can be used in rotation (insted of creating new in every new symbol showed)
    private _symbolsPool: Object;

    public canTease: boolean; // = false;
    public hasTeaseSymbol: boolean; // = false;
    private _teasing: boolean;

    protected previousReel: Reel;

    protected nextReel: Reel;

    protected shouldBeStopping: boolean;

    public useSymbolsTeaserCase: boolean;

    constructor() {
        super();

        this._symbolArray = [];

        // define default state of reel
        this._spinning = false;
        this._stopping = true;
        this.stopped = true;

        this._actualIndex = 0;

        this._startDelay = 0;
        this._stopDelay = 0;
        this._teasing = false;
        this.useSymbolsTeaserCase = false;

        this._stopTimer = CreateFrameTimer("reel");
        this._stopTimer.on(FrameTimerEvents.Complete, this.stopWithDelay.bind(this));

        this.canTease = false;
        this.hasTeaseSymbol = false;
    }

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    public configurate(node: Element): void {
        if (node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL)) {
            this._preRoll = parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL));
        } else {
            this._preRoll = 180;
        }

        if (node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED)) {
            this._preRollSpeed = parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED));
        } else {
            this._preRollSpeed = 10;
        }

        if (node.hasAttribute(ReelConfigurationAttributes.POST_ROLL)) {
            this._postRoll = parseInt(node.getAttribute(ReelConfigurationAttributes.POST_ROLL));
        } else {
            this._postRoll = 180;
        }

        if (node.hasAttribute(ReelConfigurationAttributes.POST_ROLL_SPEED)) {
            this._postRollSpeed = parseInt(node.getAttribute(ReelConfigurationAttributes.POST_ROLL_SPEED));
        } else {
            this._postRollSpeed = 5;
        }

        if (node.hasAttribute(ReelConfigurationAttributes.SPEED)) {
            this._speed = parseInt(node.getAttribute(ReelConfigurationAttributes.SPEED));
        } else {
            this._speed = 3;
        }

        if (node.hasAttribute(ReelConfigurationAttributes.FORCE_SPEED_MULTIPLER)) {
            this._forceSpeedMultipler = parseFloat(
                node.getAttribute(ReelConfigurationAttributes.FORCE_SPEED_MULTIPLER),
            );
        } else {
            this._forceSpeedMultipler = 1.5;
        }
    }

    /**
     * Set new symbols from response based on reel symbols.
     * @method setStoppedSymbols
     * @param reelData IReel
     * @return void
     */
    public setStoppedSymbols(reelData: IReel): void {
        ArgValidator.ensureIsNotNullOrUndefined(reelData, "reelData");

        this._stoppedSymbolArray = reelData.symbols;
    }

    /**
     * Get new symbols from response based on reel symbols.
     * @method getStoppedSymbols
     * @return Array<number>
     */
    public getStoppedSymbols(): Array<number> {
        return this._stoppedSymbolArray;
    }

    /**
     * Set symbols data from prepared symbols textured.
     * @method setSymbolsData
     * @return void
     * @param symbolsData
     */
    public setSymbolsData(symbolsData: Object[]): void {
        this._symbolsPool = {};

        for (let i = 0; i < symbolsData.length; i++) {
            this._symbolsPool[i] = {};

            let j: number;
            let symbol: ImageElement;

            if (symbolsData[i][Symbol.CaseRotation]) {
                this._symbolsPool[i][Symbol.CaseRotation] = [];
                for (j = 0; j < this._visibleSymbolCount + 2; j++) {
                    symbol = new ImageElement(symbolsData[i][Symbol.CaseRotation]);
                    symbol.visible = false;
                    symbol.usedInPool = false;

                    symbol.position.x = this._symbolWidth / 2;
                    symbol.pivot.x = symbol.width / 2;
                    symbol.position.y = this._symbolHeight / 2;
                    symbol.pivot.y = symbol.height / 2;

                    this.addChild(symbol);
                    this._symbolsPool[i][Symbol.CaseRotation].push(symbol);
                }
            }

            if (symbolsData[i][Symbol.CaseBlur]) {
                this._symbolsPool[i][Symbol.CaseBlur] = [];
                for (j = 0; j < this._visibleSymbolCount + 2; j++) {
                    symbol = new ImageElement(symbolsData[i][Symbol.CaseBlur]);
                    symbol.visible = false;
                    symbol.usedInPool = false;

                    symbol.position.x = this._symbolWidth / 2;
                    symbol.pivot.x = symbol.width / 2;
                    symbol.position.y = this._symbolHeight / 2;
                    symbol.pivot.y = symbol.height / 2;

                    this.addChild(symbol);
                    this._symbolsPool[i][Symbol.CaseBlur].push(symbol);
                }
            }

            if (symbolsData[i][Symbol.CaseBlurTeaser]) {
                this._symbolsPool[i][Symbol.CaseBlurTeaser] = [];
                for (j = 0; j < this._visibleSymbolCount + 2; j++) {
                    symbol = new ImageElement(symbolsData[i][Symbol.CaseBlurTeaser]);
                    symbol.visible = false;
                    symbol.usedInPool = false;

                    symbol.position.x = this._symbolWidth / 2;
                    symbol.pivot.x = symbol.width / 2;
                    symbol.position.y = this._symbolHeight / 2;
                    symbol.pivot.y = symbol.height / 2;

                    this.addChild(symbol);
                    this._symbolsPool[i][Symbol.CaseBlurTeaser].push(symbol);
                }
            }

            if (symbolsData[i][Symbol.CaseRotationTeaser]) {
                this._symbolsPool[i][Symbol.CaseRotationTeaser] = [];
                for (j = 0; j < this._visibleSymbolCount + 2; j++) {
                    symbol = new ImageElement(symbolsData[i][Symbol.CaseRotationTeaser]);
                    symbol.visible = false;
                    symbol.usedInPool = false;

                    symbol.position.x = this._symbolWidth / 2;
                    symbol.pivot.x = symbol.width / 2;
                    symbol.position.y = this._symbolHeight / 2;
                    symbol.pivot.y = symbol.height / 2;

                    this.addChild(symbol);
                    this._symbolsPool[i][Symbol.CaseRotationTeaser].push(symbol);
                }
            }
        }
    }

    private getFromSymbolsPool(id: number, type: string): ImageElement {
        if (this._symbolsPool != null && this._symbolsPool[id] != null && this._symbolsPool[id][type] != null) {
            let i = 0;
            while (this._symbolsPool[id][type][i] != null) {
                if (!this._symbolsPool[id][type][i].usedInPool) {
                    /* this._symbolsPool[id][type][i].tint = 0xFFFFFF;
                    this._symbolsPool[id][type][i].scale.x = 1;
                    this._symbolsPool[id][type][i].scale.y = 1;*/
                    this._symbolsPool[id][type][i].usedInPool = true;
                    this._symbolsPool[id][type][i].visible = true;
                    this._symbolsPool[id][type][i].alpha = 1;
                    return this._symbolsPool[id][type][i];
                }
                i++;
            }
        }
        return null;
    }

    private removeFromSymbolsPool(symbol: ImageElement): void {
        Tweens.removeTweens(symbol, "position");
        Tweens.removeTweens(symbol, "scale");
        /*symbol.tint = 0xFFFFFF;
        symbol.alpha = 1;
        symbol.scale.x = 1;
        symbol.scale.y = 1;*/
        symbol.visible = false;
        symbol.usedInPool = false;
    }

    /**
     * Set default symbol width.
     * @method setSymbolWidth
     * @param value number
     * @return void
     */
    public setSymbolWidth(value: number): void {
        this._symbolWidth = value;
    }

    /**
     * Set default symbol height.
     * @method setSymbolHeight
     * @param value number
     * @return void
     */
    public setSymbolHeight(value: number): void {
        this._symbolHeight = value;
    }

    /**
     * Set reel index for dispatchers.
     * @method setReelIndex
     * @param value number
     * @return void
     */
    public setReelIndex(value: number): void {
        this._reelIndex = value;
    }

    /**
     * Set start delay in frames.
     * @method setStartDelay
     * @param time number
     * @return void
     */
    public setStartDelay(time: number): void {
        this._startDelay = time;
    }

    /**
     * Set stop delay in frames.
     * @method setStopDelay
     * @param frames number
     * @return void
     */
    public setStopDelay(frames: number): void {
        this._stopDelay = frames;
    }

    /**
     * Set array of symbols indexes for rotation.
     * @method setStartDelay
     * @param array Array<number>
     * @return void
     */
    public setReel(array: Array<number>): void {
        this._reel = array;
    }

    /**
     * Set number of symbols visible on reel.
     * @method setVisibleSymbolCount
     * @param value number
     * @return void
     */
    public setVisibleSymbolCount(value: number): void {
        this._visibleSymbolCount = value;
    }

    public setPreviousReel(value: Reel): void {
        this.previousReel = value;
    }

    public setNextReel(value: Reel): void {
        this.nextReel = value;
    }

    /**
     * Show static symbols on reel before spinning or after stopping based on reel symbols from response.
     * @method showSymbols
     * @param reelData IReel
     * @return void
     */
    public showSymbols(reelData: IReel): void {
        this.visible = true;
        this.shouldBeStopping = false;

        if (this._spinning === false) {
            // remove all symbols and add new symbols
            if (this._symbolArray != null && this._symbolArray.length > 0) {
                for (let i = 0; i < this._symbolArray.length; i++) {
                    this.removeFromSymbolsPool(this._symbolArray[i]);
                    this._symbolArray[i] = null;
                }
            }

            Tweens.removeTweens(this); // remove tweens from all children

            this._symbolArray = [];

            let symbolNumber: number;
            let index: number;
            let symbol: ImageElement;
            for (let j = -1; j < this._visibleSymbolCount + 1; j++) {
                // get symbol id
                if (j === -1) {
                    if (this._lastTopIndex != null) {
                        index = this._lastTopIndex;
                    } else {
                        index = reelData.index - 1;
                        if (index < 0) {
                            index = this._reel.length - 1;
                        }
                    }
                    if (index > this._reel.length - 1) {
                        symbolNumber = this._reel[this._reel.length - 1];
                    } else {
                        symbolNumber = this._reel[index];
                    }
                } else if (j >= reelData.symbols.length) {
                    index = reelData.index + this._visibleSymbolCount;
                    if (index >= this._reel.length) {
                        index = this._reel[0];
                    }
                    symbolNumber = this._reel[index];
                } else {
                    symbolNumber = reelData.symbols[j];
                }

                symbol = this.getFromSymbolsPool(symbolNumber - 1, Symbol.CaseRotation);
                symbol["symbolID"] = symbolNumber;

                if (symbol != null) {
                    symbol.position.y = this._symbolHeight / 2 + this._symbolHeight * j;
                    this._symbolArray.push(symbol);
                }
            }

            this._actualIndex = reelData.index + this._visibleSymbolCount + 1;
        } else {
            if (DEBUG) {
                this.log("WARNING trying to show static symbols when spinning");
            }
        }
    }

    /**
     * Start spinning. Use tween to start spinning. Reel can start with pre roll tween.
     * @method spinStart
     * @return void
     */
    public spinStart(): void {
        if (DEBUG) {
            this.log("reel: " + this._reelIndex + " spinStart");
        }

        if (this._spinning === false) {
            this._spinning = true;
            this._stopping = false;
            this.stopped = false;
            this._teasing = false;
            this.useSymbolsTeaserCase = false;

            this._forceSpeed = this._speed;

            this._itemCounter = 0;
            this._stoppingCounter = 0;

            this.preRoll();
        } else {
            if (DEBUG) {
                this.log("ERROR trying to start spinning before this reel " + this._reelIndex + " was stopped");
            }
        }
    }

    /**
     * Start stopping rotation of reel.
     * @method spinStop
     * @param force boolean - optional
     * @param customDelay
     * @return void
     */
    public spinStop(force = false, customDelay = 0): void {
        if (DEBUG) {
            this.log("reel: " + this._reelIndex + ", spinStop: " + force + ", delay: " + this._stopDelay);
        }

        if (force) {
            this._forceSpeed = 2;
        }

        if (this._stopDelay != null && force === false) {
            this._stopTimer.stop();
            if (customDelay !== 0) {
                this._stopTimer.start(customDelay);
            } else {
                this._stopTimer.start(this._stopDelay);
            }
        } else {
            this._stopTimer.stop();
            this.shouldBeStopping = true;
        }
    }

    public getStopDuration(): number {
        const stoppingSymbols = 2 + this._visibleSymbolCount - 1;
        return this._stopDelay + stoppingSymbols * this._speed + this._postRollSpeed * 2;
    }

    /**
     * Slow down teased reel
     * @param {number} time
     */
    public teas(time = 90): void {
        this._forceSpeed = this._forceSpeed * this._forceSpeedMultipler;
        this._teasing = true;
        this._stopping = false;
        this._stopTimer.start(time);
    }

    /**
     * Delay on stopping reel if was set from machine.
     * @method stopWithDelay
     * @return void
     */
    private stopWithDelay(): void {
        if (DEBUG) {
            this.log("reel: " + this._reelIndex + " stopWithDelay");
        }

        this.shouldBeStopping = true;
        this._stopping = true;
    }

    /**
     * Pre roll of rotation.
     * @method preRoll
     * @return void
     */
    private preRoll(): void {
        if (this._symbolArray.length > 0) {
            for (let i = 0; i < this._symbolArray.length; i++) {
                // wild
                if (this._symbolArray[i]["symbolID"] === 11 || this._symbolArray[i]["symbolID"] === 12) {
                    this._symbolArray[i].visible = false;
                }

                Tweens.positionTween(this._symbolArray[i], {
                    tweenDelay: this._startDelay,
                    tweenX: [this._symbolArray[i].position.x, this._symbolArray[i].position.x],
                    tweenY: [this._symbolArray[i].position.y, this._symbolArray[i].position.y - this._preRoll],
                    tweenDuration: this._preRollSpeed,
                    callbackFunction: this.preRollComplete,
                    callbackParams: [i],
                    callbackScope: this,
                });
            }
        }
    }

    /**
     * Callback to pre roll and finishing pre rool phase of rotation.
     * @method preRollComplete
     * @param index number - index of symbol
     * @return void
     */
    private preRollComplete(index: number): void {
        if (index === 0) {
            if (this._symbolArray.length > 0) {
                this._itemCounter = 0;

                for (let i = 0; i < this._symbolArray.length; i++) {
                    this._symbolArray[i].visible = true;

                    Tweens.positionTween(this._symbolArray[i], {
                        tweenX: [this._symbolArray[i].position.x, this._symbolArray[i].position.x],
                        tweenY: [
                            this._symbolArray[i].position.y,
                            i * this._symbolHeight - this._symbolHeight + this._symbolHeight / 2,
                        ],
                        tweenDuration: this._preRollSpeed,
                        callbackFunction: this.startComplete,
                        callbackParams: [],
                        callbackScope: this,
                    });
                }
            }
        }
    }

    /**
     * Finished pre rool phase and add rotation to real start.
     * @method startComplete
     * @return void
     */
    private startComplete(): void {
        if (this._itemCounter === this._symbolArray.length - 1) {
            this.tweenReel();
        }
        this._itemCounter++;
    }

    /**
     * Rotation of symbol.
     * @method tweenReel
     * @return void
     */
    private tweenReel(): void {
        if (this._symbolArray.length > 0) {
            for (let i = 0; i < this._symbolArray.length; i++) {
                this.tweenSymbol(i);
            }
        }
    }

    private tweenSymbol(i: number): void {
        Tweens.removeTweens(this._symbolArray[i], "position");

        if (this._stopping) {
            if (this._stoppingCounter >= this._visibleSymbolCount + 2) {
                Tweens.positionTween(this._symbolArray[i], {
                    tweenX: [this._symbolArray[i].position.x, this._symbolArray[i].position.x],
                    tweenY: [this._symbolArray[i].position.y, this._symbolArray[i].position.y + this._postRoll],
                    tweenDuration: this._postRollSpeed,
                    callbackFunction: this.postRoll,
                    callbackParams: [i],
                    callbackScope: this,
                });
            } else {
                Tweens.positionTween(this._symbolArray[i], {
                    tweenX: [this._symbolArray[i].position.x, this._symbolArray[i].position.x],
                    tweenY: [this._symbolArray[i].position.y, this._symbolArray[i].position.y + this._symbolHeight],
                    tweenDuration: this._forceSpeed,
                    callbackFunction: this.updateReel,
                    callbackParams: [i],
                    callbackScope: this,
                });
            }
        } else {
            Tweens.positionTween(this._symbolArray[i], {
                tweenX: [this._symbolArray[i].position.x, this._symbolArray[i].position.x],
                tweenY: [this._symbolArray[i].position.y, this._symbolArray[i].position.y + this._symbolHeight],
                tweenDuration: this._forceSpeed,
                callbackFunction: this.updateReel,
                callbackParams: [i],
                callbackScope: this,
            });
        }
    }

    /**
     * Adding new symbol when finish step of rotation.
     * @method updateReel
     * @param index number - index of symbol
     * @return void
     */
    private updateReel(index: number): void {
        if (index === 0) {
            this.removeLastSymbolFromReel();

            if (this._actualIndex >= this._reel.length) {
                this._actualIndex = 0;
            }

            if (this.shouldBeStopping && !this._stopping) {
                if (this.previousReel === null) {
                    this._stopping = true;
                } else if (this.previousReel._stopping) {
                    this._stopping = true;
                }
            }

            if (this._stopping) {
                this._stoppingCounter++;
            }

            let symbol: ImageElement;
            let symbolID: number;
            if (this._stoppingCounter >= 2) {
                let helpIndex = this._visibleSymbolCount - 1 - (this._stoppingCounter - 2);
                if (helpIndex < 0) {
                    this._lastTopIndex = this._actualIndex;
                    symbolID = this._reel[this._actualIndex];
                    if (this._teasing && symbolID != 11) {
                        symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseRotationTeaser);

                        if (symbol === null) {
                            symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseRotation);
                        }
                    } else {
                        symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseRotation);
                    }
                } else {
                    symbolID = this._stoppedSymbolArray[helpIndex];
                    if (this._teasing && symbolID != 11) {
                        symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseRotationTeaser);

                        if (symbol === null) {
                            symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseRotation);
                        }
                        symbol.alpha = 0.5;
                    } else {
                        symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseRotation);
                    }
                }
            } else {
                symbolID = this._reel[this._actualIndex];
                if ((this._teasing || this.useSymbolsTeaserCase) && symbolID != 11) {
                    symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseBlurTeaser);

                    if (symbol === null) {
                        symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseBlur);
                    }
                    symbol.alpha = 0.5;
                } else {
                    symbol = this.getFromSymbolsPool(symbolID - 1, Symbol.CaseBlur);
                }
            }

            symbol.name = "symbolOnReelOnUpdate";

            symbol.position.y = this._symbolHeight / 2 - this._symbolHeight;

            this._symbolArray.unshift(symbol);
            this._actualIndex++;
        }

        this.tweenSymbol(index);
    }

    /**
     * Post roll of rotation.
     * @method postRoll
     * @param index number - index of symbol
     * @return void
     */
    private postRoll(index: number): void {
        if (index === 0) {
            if (this._symbolArray.length > 0) {
                for (let i = 0; i < this._symbolArray.length - 1; i++) {
                    Tweens.positionTween(this._symbolArray[i], {
                        tweenX: [this._symbolArray[i].position.x, this._symbolArray[i].position.x],
                        tweenY: [
                            this._symbolArray[i].position.y,
                            i * this._symbolHeight - this._symbolHeight + this._symbolHeight / 2,
                        ],
                        tweenDuration: this._postRollSpeed,
                        callbackFunction: this.stoppedReel,
                        callbackParams: [i],
                        callbackScope: this,
                    });
                }
            }
        }
    }

    /**
     * Finish reel rotation and call dispatchers when all symbols on reel stopped.
     * @method postRoll
     * @param index number - index of symbol
     * @return void
     */
    private stoppedReel(index: number): void {
        Tweens.removeTweens(this._symbolArray[index], "position");

        if (index === 0) {
            this.reelStopped();
        }
    }

    /**
     * Callback to stop reel rotation.
     * @method reelStoped
     * @return void
     */
    private reelStopped(): void {
        if (this.stopped === false) {
            this._stopTimer.stop();

            this._spinning = false;
            // this._stopping = false;
            this.stopped = true;
            // emit event for machine
            this.emit(Reel.ReelStopped, this._reelIndex);
        }
    }

    /**
     * Remove last symbol & tween
     * @method removeSymbolFromReel
     */
    private removeLastSymbolFromReel(): void {
        if (this._symbolArray[this._symbolArray.length - 1] != null) {
            Tweens.removeTweens(this._symbolArray[this._symbolArray.length - 1], "position");
            this.removeFromSymbolsPool(this._symbolArray[this._symbolArray.length - 1]);
        }

        this._symbolArray.pop();
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Reel.ts
 **/